import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Link,
  Stack,
  Divider,
  Card,
  CardContent,
  Grid,
  Fade,
  LinearProgress,
  Chip,
  IconButton,
  InputAdornment
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';
import { auth } from '../../services/api';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Logo from '../common/Logo';
import { alpha } from '@mui/material/styles';
import SEOHead from '../common/SEOHead';

interface PasswordValidation {
  hasMinLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  matches: boolean;
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validation, setValidation] = useState<PasswordValidation>({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    matches: false,
  });
  const navigate = useNavigate();

  // Handle ResizeObserver errors
  React.useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  const { name, email, password, confirmPassword } = formData;

  const validatePassword = (password: string, confirmPassword: string) => {
    setValidation({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&]/.test(password),
      matches: password === confirmPassword && password !== '',
    });
  };

  const isPasswordValid = () => {
    return Object.values(validation).every(value => value === true);
  };

  const getPasswordStrength = () => {
    const validCount = Object.values(validation).filter(v => v).length;
    return Math.round((validCount / 6) * 100);
  };

  const getPasswordStrengthColor = () => {
    const strength = getPasswordStrength();
    if (strength < 40) return 'error';
    if (strength < 80) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = () => {
    const strength = getPasswordStrength();
    if (strength < 40) return 'Weak';
    if (strength < 80) return 'Medium';
    return 'Strong';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'password' || name === 'confirmPassword') {
      validatePassword(
        name === 'password' ? value : password,
        name === 'confirmPassword' ? value : confirmPassword
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!isPasswordValid()) {
      setError('Please ensure all password requirements are met');
      return;
    }

    setIsLoading(true);

    try {
      const { token } = await auth.register(name, email, password);
      localStorage.setItem('token', token);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Use backend URL - dynamic based on environment
    const backendUrl = process.env.NODE_ENV === 'production' 
      ? 'https://api.altififinance.com' 
      : 'http://localhost:5000';
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  const renderPasswordRequirement = (isValid: boolean, text: string) => (
    <Box display="flex" alignItems="center" gap={1.5}>
      {isValid ? (
        <CheckCircleIcon 
          sx={{ 
            fontSize: 18,
            color: 'success.main',
            transition: 'color 0.2s ease'
          }} 
        />
      ) : (
        <RadioButtonUncheckedIcon 
          sx={{ 
            fontSize: 18,
            color: 'text.disabled',
            transition: 'color 0.2s ease'
          }} 
        />
      )}
      <Typography 
        variant="body2" 
        color={isValid ? 'success.main' : 'text.secondary'}
        sx={{ 
          fontWeight: isValid ? 500 : 400,
          transition: 'color 0.2s ease'
        }}
      >
        {text}
      </Typography>
    </Box>
  );

  const renderPasswordRequirements = () => (
    <Card 
      variant="outlined" 
      sx={{ 
        mt: 2,
        background: (theme) => theme.palette.mode === 'light' 
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        backdropFilter: 'blur(10px)',
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <CardContent sx={{ py: 3, '&:last-child': { pb: 3 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <SecurityIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="subtitle2" color="primary.main" fontWeight={600}>
            Password Requirements
          </Typography>
        </Box>
        
        {password.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Strength: {getPasswordStrengthText()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getPasswordStrength()}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getPasswordStrength()}
              color={getPasswordStrengthColor()}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  transition: 'all 0.5s ease'
                },
              }}
            />
          </Box>
        )}

        <Stack spacing={1.5}>
          {renderPasswordRequirement(validation.hasMinLength, 'At least 8 characters')}
          {renderPasswordRequirement(validation.hasUpperCase, 'One uppercase letter')}
          {renderPasswordRequirement(validation.hasLowerCase, 'One lowercase letter')}
          {renderPasswordRequirement(validation.hasNumber, 'One number')}
          {renderPasswordRequirement(validation.hasSpecialChar, 'One special character (@$!%*?&)')}
          {renderPasswordRequirement(validation.matches, 'Passwords match')}
        </Stack>
      </CardContent>
    </Card>
  );

  const registerStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Register - altifi",
    "description": "Create your free altifi account and start taking control of your financial future. Join thousands who've transformed their financial lives.",
    "url": "https://altifi.com/register",
    "isPartOf": {
      "@type": "WebSite",
      "name": "altifi",
      "url": "https://altifi.com"
    },
    "provider": {
      "@type": "Organization",
      "name": "altifi"
    }
  };

  return (
    <>
      <SEOHead 
        title="Sign Up - altifi | Start Your Financial Journey Today"
        description="Create your free altifi account and start taking control of your financial future. Smart budgeting, investment tracking, and financial insights await."
        keywords="sign up, register, create account, personal finance, budget app, financial planning, free account"
        canonicalUrl="https://altifi.com/register"
        structuredData={registerStructuredData}
      />
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: (theme) => theme.palette.mode === 'light'
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 50%, ${theme.palette.background.default} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 50%, ${theme.palette.background.default} 100%)`,
          display: 'flex',
          alignItems: 'center',
          py: 4,
          position: 'relative',

        }}
      >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
            {/* Header Card */}
            <Grid item xs={12}>
              <Card 
                elevation={0} 
                sx={{ 
                  textAlign: 'center', 
                  mb: 2,
                  background: (theme) => theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'rgba(30, 30, 30, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  boxShadow: (theme) => theme.palette.mode === 'light'
                    ? '0 8px 32px rgba(0, 0, 0, 0.08)'
                    : '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
              >
                <CardContent sx={{ py: 5 }}>
                                     <Fade in={true} timeout={1000}>
                     <Box sx={{ mb: 3 }}>
                       <Logo variant="regular" width={140} height={50} />
                     </Box>
                   </Fade>
                                     <Fade in={true} timeout={1200}>
                     <Typography 
                       component="h1" 
                       sx={{ 
                         fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                         fontWeight: 800,
                         fontFamily: '"Inter", "IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
                         letterSpacing: '-0.02em',
                         lineHeight: 1.1,
                         background: (theme) => theme.palette.mode === 'light'
                           ? 'linear-gradient(135deg, #7C8B6F 0%, #A4B494 30%, #8B7C8B 70%, #B3A4B3 100%)'
                           : 'linear-gradient(135deg, #9DAB8E 0%, #B5C4A7 30%, #A394A3 70%, #C4B7C4 100%)',
                         backgroundClip: 'text',
                         WebkitBackgroundClip: 'text',
                         WebkitTextFillColor: 'transparent',
                         textAlign: 'center',
                         mb: 2,
                         position: 'relative',
                         '&::after': {
                           content: '""',
                           position: 'absolute',
                           top: 0,
                           left: 0,
                           right: 0,
                           bottom: 0,
                           background: (theme) => theme.palette.mode === 'light'
                             ? 'linear-gradient(135deg, #7C8B6F 0%, #A4B494 30%, #8B7C8B 70%, #B3A4B3 100%)'
                             : 'linear-gradient(135deg, #9DAB8E 0%, #B5C4A7 30%, #A394A3 70%, #C4B7C4 100%)',
                           backgroundClip: 'text',
                           WebkitBackgroundClip: 'text',
                           WebkitTextFillColor: 'transparent',
                           filter: 'blur(1px)',
                           opacity: 0.3,
                           zIndex: -1,
                         },
                         textShadow: (theme) => theme.palette.mode === 'light'
                           ? '0 2px 4px rgba(124, 139, 111, 0.2)'
                           : '0 2px 4px rgba(157, 171, 142, 0.3)',
                       }}
                     >
                       Create Your Account
                     </Typography>
                   </Fade>
                  <Fade in={true} timeout={1400}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
                      Join thousands of users managing their finances with ease
                    </Typography>
                  </Fade>
                </CardContent>
              </Card>
            </Grid>

            {/* Registration Form Card */}
            <Grid item xs={12}>
              <Card 
                elevation={0}
                sx={{
                  background: (theme) => theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(30, 30, 30, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  boxShadow: (theme) => theme.palette.mode === 'light'
                    ? '0 12px 40px rgba(0, 0, 0, 0.1)'
                    : '0 12px 40px rgba(0, 0, 0, 0.4)',
                }}
              >
                <CardContent sx={{ p: 5 }}>
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    {/* Google Sign In */}
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GoogleIcon />}
                      onClick={handleGoogleSignIn}
                      sx={{ 
                        py: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        borderColor: 'divider',
                        borderWidth: 2,
                        borderRadius: 3,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderColor: 'primary.main',
                          borderWidth: 2,
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Continue with Google
                    </Button>

                    <Divider sx={{ my: 2 }}>
                      <Chip 
                        label="or register with email" 
                        size="small" 
                        sx={{ 
                          backgroundColor: 'background.paper',
                          color: 'text.secondary',
                          fontWeight: 500
                        }}
                      />
                    </Divider>

                    {/* Form Fields */}
                    <TextField
                      required
                      fullWidth
                      id="name"
                      label="Full Name"
                      name="name"
                      autoComplete="name"
                      value={name}
                      onChange={handleChange}
                      disabled={isLoading}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          '&.Mui-focused': {
                            color: 'primary.main',
                            fontWeight: 600,
                          },
                        },
                      }}
                    />

                    <TextField
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      type="email"
                      value={email}
                      onChange={handleChange}
                      disabled={isLoading}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          '&.Mui-focused': {
                            color: 'primary.main',
                            fontWeight: 600,
                          },
                        },
                      }}
                    />

                    <TextField
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={handleChange}
                      disabled={isLoading}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: 'text.secondary' }}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          '&.Mui-focused': {
                            color: 'primary.main',
                            fontWeight: 600,
                          },
                        },
                      }}
                      inputProps={{ 'data-testid': 'password-input' }}
                    />

                    <TextField
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={handleChange}
                      disabled={isLoading}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              sx={{ color: 'text.secondary' }}
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          '&.Mui-focused': {
                            color: 'primary.main',
                            fontWeight: 600,
                          },
                        },
                      }}
                      inputProps={{ 'data-testid': 'confirm-password-input' }}
                    />

                    {renderPasswordRequirements()}

                    {error && (
                      <Fade in={!!error}>
                        <Alert 
                          severity="error" 
                          sx={{ 
                            mt: 2,
                            borderRadius: 3,
                            '& .MuiAlert-message': {
                              fontSize: '1rem',
                            },
                          }}
                        >
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mt: 3, 
                        mb: 2,
                        textAlign: 'center',
                        fontSize: '0.875rem',
                        lineHeight: 1.5
                      }}
                    >
                      By creating an account, you agree to our{' '}
                      <Link 
                        component={RouterLink} 
                        to="/terms"
                        sx={{ 
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Terms of Service
                      </Link>
                      {' '}and{' '}
                      <Link 
                        component={RouterLink} 
                        to="/privacy"
                        sx={{ 
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={isLoading || !name || !email || !isPasswordValid()}
                      sx={{ 
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        mt: 3,
                        borderRadius: 3,
                        background: (theme) => theme.palette.mode === 'light'
                          ? 'linear-gradient(135deg, #7C8B6F 0%, #4CAF50 100%)'
                          : 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                        boxShadow: '0 4px 16px rgba(46, 125, 50, 0.3)',
                        '&:hover': {
                          background: (theme) => theme.palette.mode === 'light'
                            ? 'linear-gradient(135deg, #1B5E20 0%, #7C8B6F 100%)'
                            : 'linear-gradient(135deg, #7C8B6F 0%, #4CAF50 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(46, 125, 50, 0.4)',
                        },
                        '&:disabled': {
                          background: 'action.disabledBackground',
                          color: 'action.disabled',
                          boxShadow: 'none',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {isLoading ? 'Creating Your Account...' : 'Create Account'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Sign In Link Card */}
            <Grid item xs={12}>
              <Card 
                variant="outlined" 
                sx={{ 
                  textAlign: 'center',
                  background: (theme) => theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(30, 30, 30, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                }}
              >
                <CardContent sx={{ py: 3, '&:last-child': { pb: 3 } }}>
                  <Typography variant="body1" color="text.secondary">
                    Already have an account?{' '}
                    <Link 
                      component={RouterLink} 
                      to="/login"
                      sx={{ 
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.dark',
                        },
                        transition: 'color 0.2s ease'
                      }}
                    >
                      Sign in here
                    </Link>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
                      </Grid>
       </Container>
     </Box>
    </>
  );
};

export default Register; 